import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { TagColored, TagGeneral, Search, sparkToast } from '@spark';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import '@/features/datasets/components/DataSelection/EntityTagLegend.css';

/**
 * EntityTagLegend — list of every possible entity tag for the "highlight words
 * to annotate" flow, so users can see the available labels (and how many of each
 * already exist) before highlighting text. Each tag carries a delete button that
 * removes the category (and its entities) via `onDeleteCategory`.
 *
 * Action bar (shown when not in pending-selection mode):
 * - Search input: filters the visible tags by substring match
 * - "+ New Tag" button: clears the search and opens an inline name editor at
 *   the top-left of the tags list
 */
const EntityTagLegend = ({
    allCategories = [],
    categoryCounts = {},
    onDeleteCategory,
    onAddTag,
    onSelectTag,
    selectedTag = null,
    canDelete = false,
    hasPendingSelection = false,
}) => {
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const inputRef = useRef(null);
    const itemsRef = useRef(null);
    // Tracks the tallest height the items container has reached while unfiltered,
    // so the container never shrinks below that when tags are filtered.
    const [itemsMinHeight, setItemsMinHeight] = useState(undefined);

    const handleTagClick = (e, cat) => {
        // Let the tag's own delete button handle its click.
        if (e.target.closest('.spark-tag-colored-delete')) return;
        onSelectTag?.(selectedTag === cat ? null : cat);
    };

    useEffect(() => {
        if (adding && inputRef.current) inputRef.current.focus();
    }, [adding]);

    // When transitioning into pending-selection mode, reset search so the full
    // unfiltered list is shown with the "Select a tag…" prompt.
    useEffect(() => {
        if (hasPendingSelection) {
            setSearchQuery('');
        }
    }, [hasPendingSelection]);

    // Capture the natural (unfiltered) items height and lock it as min-height so
    // the tags area — and therefore the modal — never shrink during a search.
    // We re-measure whenever the full tag count changes (tag added/deleted) and
    // keep the high-water mark so an ever-larger set never regresses.
    useLayoutEffect(() => {
        if (!searchQuery && itemsRef.current) {
            const h = itemsRef.current.offsetHeight;
            setItemsMinHeight((prev) => Math.max(prev ?? 0, h));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allCategories.length, searchQuery]);

    if (!allCategories.length) return null;

    const filteredCategories = searchQuery
        ? allCategories.filter((cat) => cat.toLowerCase().includes(searchQuery.toLowerCase()))
        : allCategories;

    const startAdding = () => {
        setSearchQuery('');
        setDraft('');
        setAdding(true);
    };

    const cancelAdding = () => {
        setAdding(false);
        setDraft('');
    };

    const commitAdding = () => {
        const name = draft.trim();
        if (name) {
            const isDuplicate = allCategories.some(
                (cat) => cat.toLowerCase() === name.toLowerCase()
            );
            if (isDuplicate) {
                sparkToast.warning('Tag already exists');
                setAdding(false);
                setDraft('');
                return;
            }
            onAddTag?.(name);
        }
        setAdding(false);
        setDraft('');
    };

    return (
        <div className={['ner-tag-legend', hasPendingSelection ? 'ner-tag-legend--pending' : ''].filter(Boolean).join(' ')}>
            <span className="ner-tag-legend-title">
                {hasPendingSelection ? 'Select a tag to apply to the highlighted text' : 'Available tags'}
            </span>

            {!hasPendingSelection && (
                <div className="ner-tag-legend-toolbar">
                    <Search
                        size="small"
                        placeholder="Search tags"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClear={() => setSearchQuery('')}
                        className="ner-tag-legend-search"
                    />
                    {canDelete && (
                        <button
                            type="button"
                            className="ner-tag-legend-add-btn"
                            onClick={startAdding}
                        >
                            <TagGeneral variant="add-new" className="ner-tag-legend-add" />
                        </button>
                    )}
                </div>
            )}

            <div
                ref={itemsRef}
                className="ner-tag-legend-items"
                style={itemsMinHeight !== undefined ? { minHeight: itemsMinHeight } : undefined}
            >
                {canDelete && adding && (
                    <input
                        ref={inputRef}
                        className="ner-tag-legend-add-input"
                        value={draft}
                        placeholder="New tag"
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                commitAdding();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelAdding();
                            }
                        }}
                        onBlur={cancelAdding}
                    />
                )}
                {filteredCategories.map((cat) => (
                    <span
                        key={cat}
                        className={['ner-tag-legend-item', selectedTag === cat ? 'ner-tag-legend-item--selected' : '']
                            .filter(Boolean)
                            .join(' ')}
                        onClick={(e) => handleTagClick(e, cat)}
                    >
                        <TagColored
                            label={categoryCounts[cat] ? `${cat} (${categoryCounts[cat]})` : cat}
                            variant={getSparkTagVariant(cat, allCategories)}
                            showSuffixIcon={canDelete}
                            onDelete={() => onDeleteCategory?.(cat)}
                            className={selectedTag === cat ? 'ner-tag-legend-tag-selected' : ''}
                        />
                    </span>
                ))}
                {filteredCategories.length === 0 && searchQuery && (
                    <span className="ner-tag-legend-empty">No tags match &quot;{searchQuery}&quot;</span>
                )}
            </div>
        </div>
    );
};

export default EntityTagLegend;
